import { GetServerSideProps } from 'next'
import { NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useMount } from 'react-use'

import { getLocale } from '@/lib/i18n-utils'
import { AiImageGenerator } from '@/components/tools/ai-art-generator/AiImageGenerator'
import {
  AI_IMAGE_GENERATOR_GENERATE_STATE,
  ImageSize,
  ImageStyle,
} from '@/constants/tools/ai-image-generator'
import { findOneToolsAiImageGeneratorJob } from '@/lib/tools/db/queries/tools-ai-image-generator-jobs'

type PageProps<TProps extends {}> = {
  pageProps: TProps
}

type AiImageGeneratorPageProps = {
  topic: string | null

  redirected?: {
    input: {
      topic: string
      imageStyle: ImageStyle
      imageSize: ImageSize
    }
    image: {
      url: string
      width: number
      height: number
    }
    error: string
  }
}

export const getServerSideProps: GetServerSideProps<
  AiImageGeneratorPageProps
> = async (context) => {
  const locale = await getLocale(context)

  const query = context.query

  const topic = Array.isArray(query.topic)
    ? query.topic[0]
    : query.topic ?? null

  const redirected: {
    redirected?: {
      input: {
        topic: string
        imageStyle: ImageStyle
        imageSize: ImageSize
      }
      image: {
        url: string
        width: number
        height: number
      }
      error: string
    }
  } = {}

  /**
   * 프로젝트 생성 중 오류가 발생하면 이 페이지로 이동됨\
   * 그 때 jobId를 함께 넘겨 받음\
   * 해당 job을 조회하여 입력된 topic, image, error를 클라이언트로 전달
   */
  const jobId = Array.isArray(query.jobId) ? query.jobId[0] : query.jobId

  if (!!jobId) {
    const foundJob = await findOneToolsAiImageGeneratorJob(jobId)

    if (
      !!foundJob &&
      foundJob.generateState === AI_IMAGE_GENERATOR_GENERATE_STATE.ERROR
    ) {
      redirected.redirected = {
        input: {
          topic: foundJob.topic,
          imageStyle: foundJob.imageStyle,
          imageSize: foundJob.imageSize,
        },
        image: foundJob.generatedImage,
        error: foundJob.error ?? '',
      }
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      topic,
      ...redirected,
    },
  }
}

const AiImageGeneratorPage: NextPage<PageProps<AiImageGeneratorPageProps>> = ({
  pageProps: { topic, redirected },
}) => {
  /**
   * 쿼리 지우기
   */
  const router = useRouter()

  useMount(() => {
    router.replace('', undefined, { shallow: true })
  })

  return (
    <>
      <Head>
        <title>AI STUDIOS - AI Image Generator</title>
      </Head>

      <AiImageGenerator topic={topic || undefined} redirected={redirected} />
    </>
  )
}

export default AiImageGeneratorPage
